import { PickerColumnItem } from 'antd-mobile/es/components/picker';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { SelectPicker } from '../../../../components/common/inputs/SelectPicker';
import { AnimateLoading } from '../../../../components/common/loading/AnimateLoading';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import { SurveyPickerButton } from '../../../../components/common/survey/inputs/SuveyPickerButton';
import { SearchModelModal } from '../../../../components/custom/modal/toast/SearchModelModal';
import { useGetBrandsQuery } from '../../../../hooks/api/brand/queries/useGetBrands.query';
import { useGetMachinesQuery } from '../../../../hooks/api/machines/queries/useGetMachines.query';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue } from '../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

export const SellTractorSurveyRequiredInputsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userSellTractorStates = useAppSelector((state) => state.userSellTractorSurvey);
  const userSellTractorActions = userSellTractorSurveySlice.actions;

  const [selectedBrand, setSelectedBrand] = useState<PickerColumnItem>();

  const [model, setModel] = useState<string>();
  const [machineSearchTerm, setMachineSearchTerm] = useState<string>();
  const [engineOutputInPs, setEngineOutputInPs] = useState<number>();
  const [manufacturedYear, setReleaseYear] = useState<number>();
  const [operationTime, setOperationTime] = useState<number>();
  const [isBrandsPickerVisible, setIsBrandsPickerVisible] = useState<boolean>(false);
  const [isModelSearchModalVisible, setIsModelSearchModalVisible] = useState<boolean>(false);

  const { data: brands, isLoading: isGetBrandsQueryLoading } = useGetBrandsQuery();

  const { data: machines } = useGetMachinesQuery({
    searchParam: {
      brandId: selectedBrand?.value as string,
      fields: 'id,model,engineOutputInPs,approvalStatus',
      searchTerm: machineSearchTerm ?? '',
    },
    options: { enabled: !!selectedBrand, cacheTime: 1000 * 60 * 5 },
  });

  const transformedBrandsForPicker = useMemo<{ label: string; value: string }[][]>(() => {
    if (brands) {
      const transformedBrands = brands.map((brand) => {
        return {
          label: brand.name,
          value: brand.id,
        };
      });

      return [transformedBrands];
    }

    return [];
  }, [brands]);

  useEffect(() => {
    const { brand, brandId, model, engineOutputInPs, operationTime, manufacturedYear } = userSellTractorStates;
    if (brand) {
      setSelectedBrand({ label: brand, value: brandId as string });
    }
    setModel(model);
    setEngineOutputInPs(engineOutputInPs);
    setOperationTime(operationTime);
    setReleaseYear(manufacturedYear);
  }, [userSellTractorStates]);

  useEffect(() => {
    if (model !== undefined) {
      setMachineSearchTerm(model);
    }

    if (machines && model) {
      const lowerCaseModel = model.toLocaleLowerCase();

      const machineData = machines
        .map((machine) => {
          return { ...machine, model: machine.model.toLocaleLowerCase() };
        })
        .find((machine) => machine.model === lowerCaseModel);

      if (machineData) {
        setEngineOutputInPs(machineData.engineOutputInPs);
      }
    }
  }, [model]);

  if (isGetBrandsQueryLoading) {
    return <AnimateLoading />;
  }

  const isConfirmAllowed = () => {
    const hasBasicInfo = !!selectedBrand && !!model && !!engineOutputInPs && !!manufacturedYear && !!operationTime;

    return hasBasicInfo;
  };

  if (brands) {
    const { sellTractorIdForEdit } = userSellTractorStates;

    return (
      <>
        <SellTractorSurveyPageWrapper
          headerTitle={'매물의 필수 정보를\n알려주세요!'}
          confirmButtonText={'완료'}
          canConfirm={isConfirmAllowed()}
          confirmHandler={() => {
            dispatch(
              userSellTractorActions.changeSellTractorRequiredInputs({
                brand: selectedBrand?.label as string,
                brandId: selectedBrand?.value as string,
                model: model as string,
                engineOutputInPs: engineOutputInPs as number,
                manufacturedYear: manufacturedYear as number,
                operationTime: operationTime as number,
              }),
            );
            navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true });
          }}
          closeHandler={() => {
            navigate('/survey/progress/sell-tractor/required-input-overview', { replace: true });
          }}
        >
          <SurveyForm gap={0}>
            <SurveyInputSection>
              <BasicInputWithExtras
                inputLabel={'브랜드'}
                type={'text'}
                inputValue={selectedBrand?.label as string}
                readonly
                placeholder={'목록에서 선택'}
              >
                <SurveyPickerButton
                  buttonText={'목록 선택'}
                  disabled={!!sellTractorIdForEdit}
                  onClick={() => setIsBrandsPickerVisible(true)}
                />
              </BasicInputWithExtras>
            </SurveyInputSection>

            <SurveyInputSection>
              <BasicInputWithExtras
                className={`${!!selectedBrand && !sellTractorIdForEdit && 'bg-white'} pointer`}
                inputLabel={'모델명'}
                inputValue={model}
                readonly
                placeholder={'모델명을 입력해 주세요. 예) 6155R'}
                onClick={() => !!selectedBrand && !sellTractorIdForEdit && setIsModelSearchModalVisible(true)}
              />
            </SurveyInputSection>

            <SurveyInputSection>
              <SurveyInputHeader inputTitle={'마력'} />
              <BasicInputWithExtras
                type={'number'}
                inputValue={engineOutputInPs ?? ''}
                readonly={!!sellTractorIdForEdit}
                placeholder={'예) 155'}
                onChange={(value: InputValue) => setEngineOutputInPs(value as number)}
              >
                <Surfix>{'마력'}</Surfix>
              </BasicInputWithExtras>
            </SurveyInputSection>

            <SurveyInputSection>
              <SurveyInputHeader inputTitle={'연식'} />
              <BasicInputWithExtras
                type={'number'}
                inputValue={manufacturedYear}
                placeholder={'예) 2017'}
                onChange={(value: InputValue) => setReleaseYear(value as number)}
              >
                <Surfix>{'년식'}</Surfix>
              </BasicInputWithExtras>
            </SurveyInputSection>

            <SurveyInputSection>
              <SurveyInputHeader inputTitle={'사용시간'} />
              <BasicInputWithExtras
                type={'number'}
                inputValue={operationTime}
                placeholder={'예) 1,350'}
                onChange={(value: InputValue) => setOperationTime(value as number)}
              >
                <Surfix>{'시간'}</Surfix>
              </BasicInputWithExtras>
            </SurveyInputSection>
          </SurveyForm>
        </SellTractorSurveyPageWrapper>

        <SelectPicker
          columns={transformedBrandsForPicker}
          visible={isBrandsPickerVisible}
          onConfirm={(selectedBrandName, extendBrandValue) => {
            setSelectedBrand(extendBrandValue.items[0] as PickerColumnItem);
            setModel('');
            setEngineOutputInPs(undefined);
          }}
          onClose={() => setIsBrandsPickerVisible(false)}
        />

        <SearchModelModal
          isSlideModalOpen={isModelSearchModalVisible}
          closeAll={() => setIsModelSearchModalVisible(false)}
          machines={machines}
          model={model}
          changeModel={(newModel: string) => setModel(newModel)}
          machineSearchTerm={machineSearchTerm}
          changeMachineSearchTerm={(newSearchTerm: string) => setMachineSearchTerm(newSearchTerm)}
        />
      </>
    );
  }

  return <></>;
};

const SurveyInputSection = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px) 24px var(--radius-large, 16px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Surfix = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */

  white-space: nowrap;
`;
